import {useForm} from 'react-hook-form'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import FormWrapper from '../../shared/FormWrapper'
import Input from '../../shared/Input'

import {RESOURCE_PATTERN, isSignInScope, normalizeResource, scopePermissions} from '../../../lib/oauthScopes'

const resourceHint = resource => {
  const normalized = normalizeResource(resource)

  if (normalized && normalized !== resource.trim())
    return `Saved as "${normalized}". Scope names are always lowercase.`

  return 'The thing a client wants to reach, as one word. Letters, numbers, - and _.'
}

/**
 * Shows what the scope being typed will create before it is created: the permission names a client
 * will request, or that a sign-in scope carries none.
 */
const Preview = ({resource, permissions}) => {
  if (!resource)
    return <Typography variant='body2'>Type a resource to see what clients will request.</Typography>

  if (isSignInScope({resource}))
    return (
      <Typography variant='body2'>
        A sign-in scope with no read or write permission. Clients request it as <strong>{resource}</strong>.
      </Typography>
    )

  const created = scopePermissions({resource, permissions})

  if (created.length === 0)
    return <Typography variant='body2'>Read and write permissions are created with it.</Typography>

  return created.map(permission => (
    <Stack key={permission.category} className={`permission ${permission.category}`}>
      <Typography variant='subtitle2'>{permission.name}</Typography>
      <Typography variant='body2'>{permission.description}</Typography>
    </Stack>
  ))
}

const ScopeForm = props => {
  const methods = useForm({mode: 'onChange'})
  const {errors, isValid} = methods.formState
  const resource = methods.watch('resource') || ''
  const previewResource = errors.resource ? '' : normalizeResource(resource)

  return (
    <Stack className='scope-form'>
      <FormWrapper methods={methods} handleSubmit={props.handleSubmit}>
        <Input
          error={!!errors.resource}
          label='Resource'
          name='resource'
          placeholder='e.g. contacts'
          options={{
            required: 'Resource is required',
            validate: {
              oneWord: value => RESOURCE_PATTERN.test(normalizeResource(value)) ||
                'Use one word. Spaces and colons would break the scope name clients send.',
              unused: value => !props.resources.includes(normalizeResource(value)) ||
                `A scope named "${normalizeResource(value)}" already exists.`
            }
          }}
          helperText={errors.resource?.message || resourceHint(resource)}
          margin='dense'
        />
        <Input
          error={!!errors.description}
          label='Description'
          name='description'
          type='textarea'
          placeholder='e.g. Your saved contacts and their details'
          options={{
            required: 'Description is required',
            validate: value => !!value.trim() || 'Description is required'
          }}
          helperText={errors.description?.message || 'What this gives access to, in words a user would understand.'}
          multiline
          rows={3}
          margin='dense'
        />
        <Paper className='preview' elevation={0} sx={{bgcolor: 'Background'}}>
          <Typography className='caption' variant='caption'>What this creates</Typography>
          <Preview resource={previewResource} permissions={props.permissions} />
        </Paper>
        {props.error && <Alert className='notice' severity='error'>{props.error}</Alert>}
        <Button
          disabled={props.loading || !isValid}
          type='submit'
          variant='contained'
          sx={{color: '#F7F7F7'}}>
          Create scope
        </Button>
      </FormWrapper>
    </Stack>
  )
}

export default ScopeForm
